import modal from '@mx/vix/modal';
import toast from '@mx/vix/toast';
import { DisplayType } from '@/common/enum';
import { env, AccountStatus, bridge, checkUrl, analytics, getStorage, removeStorage, monitor, request } from '@/common';
import { analyticConfig } from '@/config/analyticConfig';
import { reportBlackType, refreshPageSubscribe } from '@/service/fetchIndexData';
import accountCheckPopupService from '@/components/accountCheckPopup/function-call';

let handleClick: () => void;

/**
 * @description 移除 title 中的换行符
 * @description (在 https://km.sankuai.com/collabpage/2730523938 需求后, 后端在展示半浮层时会加入 '\n' 换行符, 而展示弹窗时并不需要换行符, 这里是防御性编程)
 * @param title 原始 title
 * @returns 处理后的 title
 */
function removeNewlineFromTitle(title: string): string {
  if (!title) return '';
  return title.replace(/\n/g, '');
}

/**
 * @description 账户管控状态处理
 * @description 这部分的逻辑梳理, 可以看该技术方案 wiki: https://km.sankuai.com/collabpage/2730523938 (2025/11/03)
 * @param options
 * @param   data 接口返回管控信息
 * @param   type 操作类型，充值或者提现
 * @param   url  实名操作后的callback_url
 * @param   cid 页面 cid ，埋点上报用
 * @param   refresh 页面数据刷新
 * @param   isToCallBackUrl 是否跳转到url页面
 * @param   fromPage 页面来源
 * @param   extend
 * @returns void
 */
export function accountCheck(options: AccountCheckOptions) {
  const { data, type, url, cid, refresh, isToCallBackUrl, fromPage, extend, onCancel } = options;

  const GUIDE_INFO = data[type] || {};
  const title = GUIDE_INFO.title;
  // 处理html字符串中的dom结构
  const contentEle = document.createElement('div');
  contentEle.innerHTML = GUIDE_INFO.body;
  const tagA = contentEle.querySelector('a');
  const tagUrl = tagA?.getAttribute('href') || '';
  tagA?.classList.add('modal-link');
  tagA?.removeAttribute('href');
  const content = contentEle.innerHTML;

  // modal 内协议地址点击跳转事件
  if (!handleClick) {
    handleClick = () => {
      let popType = '-999';
      switch (true) {
        case env.inMeituanTurbo &&
          (GUIDE_INFO.type === AccountStatus.WeakConstraint || GUIDE_INFO.type === AccountStatus.Constraint):
          popType = '-1';
          break;
        case GUIDE_INFO.type === AccountStatus.WeakConstraint:
          popType = '1';
          break;
        case GUIDE_INFO.type === AccountStatus.Constraint:
          popType = '2';
          break;
        case GUIDE_INFO.type === AccountStatus.Freeze:
          popType = '3';
          break;
        case GUIDE_INFO.type === AccountStatus.AccountConfirm:
          popType = '5';
          break;
        default:
      }
      analytics.click(analyticConfig.guideInfo.regulationMc, {
        popup_type: popType,
      });
      if (tagUrl) bridge.openPage(tagUrl);
    };
  }

  // 给 modal 内的协议跳转地址添加点击事件
  setTimeout(() => {
    const modalLink = document.querySelector('.modal-link');
    if (!modalLink) return;
    modalLink.addEventListener('click', handleClick);
  }, 500);

  // 美团极速版、MMP架构独立app 管控账户不允许操作
  if (
    env.inMeituanTurbo &&
    (GUIDE_INFO.type === AccountStatus.WeakConstraint || GUIDE_INFO.type === AccountStatus.Constraint)
  ) {
    analytics.mv(analyticConfig.guideInfo.mv, {
      popup_type: '-999',
    });
    modal.show({
      title: '暂无法充值/提现',
      content: '小程序内无法操作',
      preventScroll: true,
      className: 'guide-info-modal',
      button: {
        text: '知道了',
        onClick: () => {
          analytics.addTag(
            {
              bid: analyticConfig.guideInfo.mc,
              from: env.scene,
              utm_source: env.utmSource,
              button_name: '知道了',
              popup_type: '-999',
            },
            cid
          );
          analytics.click(analyticConfig.guideInfo.mc, {
            button_name: '知道了',
            popup_type: '-999',
          });
          modal.hide();
        },
      },
    });
    return;
  }

  // 独立验证
  // 异常时会 throw error
  const startPassVerify = async () => {
    try {
      toast.show('loading', '', { delay: 0 });

      const params = {
        merchantNo: '1008',
        partnerId: '12',
        scene: 108,
        hideHeader: false,
      };

      await import('@mx/pass-verify/build/style.css');
      const { PassVerify } = await import('@mx/pass-verify');
      const passVerifyObj = new PassVerify(params, 'halfPage', { hideHeader: true });
      await passVerifyObj.init();
      const res = await passVerifyObj.startVerify();

      toast.hide();

      if (!res?.result?.payToken) {
        throw new Error('Missing payToken');
      }

      return res.result.payToken;
    } catch (error: any) {
      toast.hide();

      throw error;
    }
  };

  // "假"关闭支付账户
  // 异常时会 throw error
  const fakeClosePayAccount = async (param: IFakeClosePayAccountReq) => {
    const response = await request.postFakeClosePayAccount(param);

    return response;
  };

  async function fakeCloseAccount() {
    try {
      const payToken = await startPassVerify(); // 1. 进行独立验证
      await fakeClosePayAccount({ payToken }); // 2. 验证通过进行"假"关闭
      return true;
    } catch (error: any) {
      if (error?.code === 1) {
        // 用户主动取消独立验证, 错误码见 https://km.sankuai.com/collabpage/1716131107
        return false;
      }

      monitor.captureAjaxErrorMessage(error?.stack || error?.message || '[api]支付账户假关闭失败');
      toast.show('text', '网络异常, 请检查您的网络设置');

      // 若中途有错误则中断流程
      return false;
    }
  }

  // [异步函数] 提取公共的按钮回调逻辑
  const handleOnClickCancelBtn = async () => {
    analytics.addTag(
      {
        bid: analyticConfig.guideInfo.mc,
        from: env.scene,
        utm_source: env.utmSource,
        button_name: GUIDE_INFO.cancelButton,
        popup_type: String(GUIDE_INFO.type),
      },
      cid
    );
    analytics.click(analyticConfig.guideInfo.mc, {
      button_name: GUIDE_INFO.cancelButton,
      popup_type: String(GUIDE_INFO.type),
    });

    // 需要支付账户假关闭类型
    if (GUIDE_INFO.type === AccountStatus.AccountFrozenGuideMarkClosePayAccount) {
      const fakeCloseSuccess = await fakeCloseAccount();
      if (!fakeCloseSuccess) return;
    } else if (GUIDE_INFO.type === AccountStatus.ProactiveFrozenGuideMarkClosePayAccount) {
      // 证件过期冻结/解冻 类型
      return;
    }

    if (isToCallBackUrl && GUIDE_INFO.canSkip) {
      refresh();
      bridge.openPage(checkUrl(url), 1);
    }
    // 调用取消回调
    if (onCancel) onCancel();
  };

  const handleOnClickConfirmBtn = async () => {
    analytics.addTag(
      {
        bid: analyticConfig.guideInfo.mc,
        from: env.scene,
        utm_source: env.utmSource,
        button_name: GUIDE_INFO.confirmButton,
        popup_type: String(GUIDE_INFO.type),
      },
      cid
    );
    analytics.click(analyticConfig.guideInfo.mc, {
      button_name: GUIDE_INFO.confirmButton,
      popup_type: String(GUIDE_INFO.type),
    });
    // 弱管控、强管控 : 获取VERIFIED_RESULT_SUCCESS + Url添加参数scene_id
    if (GUIDE_INFO.type === AccountStatus.WeakConstraint || GUIDE_INFO.type === AccountStatus.Constraint) {
      constraintConfirmButton();
      return;
    }
    // 余额冻结：微信小程序判断 + Url添加参数scene_id
    if (
      GUIDE_INFO.type === AccountStatus.BalanceFreeze ||
      GUIDE_INFO.type === AccountStatus.AccountFrozenGuideClosePayAccountWithdraw ||
      GUIDE_INFO.type === AccountStatus.AccountFrozenGuideCanWithdraw ||
      GUIDE_INFO.type === AccountStatus.AccountFrozenGuideMarkClosePayAccount
    ) {
      balanceFreezeConfirmButton();
      return;
    }
    // 除5，6，7冻结原因的其他冻结情况，具体可参考 https://km.sankuai.com/collabpage/2616314001 冻结原因枚举
    if (GUIDE_INFO.type === AccountStatus.ProactiveFrozenGuideMarkClosePayAccount) {
      const fakeCloseSuccess = await fakeCloseAccount();
      if (!fakeCloseSuccess) return;
      refresh();
      if (isToCallBackUrl) {
        bridge.openPage(checkUrl(url), 1);
      }
      return;
    }
    // 其余情况
    refresh();
    bridge.openPage(GUIDE_INFO.guideUrl);
  };

  function handlePopUpWindow() {
    if (GUIDE_INFO.type === AccountStatus.Freeze || GUIDE_INFO.type === AccountStatus.PassportInternalAccountGuide) {
      freezePopup();
    } else if (GUIDE_INFO.type === AccountStatus.AccountConfirm) {
      accountConfirmPopup();
    } else {
      // 检查是否需要调起浮层
      if (GUIDE_INFO?.displayType === DisplayType.HalfSheet) {
        showGuideHalfSheet();
      } else {
        showGuideModal();
      }
    }
  }

  function showGuideHalfSheet() {
    // 调起浮层
    analytics.mv(analyticConfig.guideInfo.mv, {
      popup_type: String(GUIDE_INFO.type),
    });
    accountCheckPopupService.show({
      title,
      content,
      imageUrl: GUIDE_INFO?.imageUrl,
      button: [
        {
          text: GUIDE_INFO.confirmButton,
          onClick: () => {
            accountCheckPopupService.hide();
            handleOnClickConfirmBtn();
          },
        },
        {
          text: GUIDE_INFO.cancelButton,
          onClick: () => {
            accountCheckPopupService.hide();
            handleOnClickCancelBtn(); // 该函数是个异步函数, 但这种情况没必要加 await, 所以没加, 后续如果有必要, 请加上 await
          },
        },
      ],
    });
  }

  function showGuideModal() {
    // 调起弹窗
    analytics.mv(analyticConfig.guideInfo.mv, {
      popup_type: String(GUIDE_INFO.type),
    });
    modal.show({
      title: removeNewlineFromTitle(title),
      content,
      preventScroll: true,
      className: GUIDE_INFO.type === AccountStatus.BalanceFreeze ? 'freeze-info-modal' : 'guide-info-modal',
      button: [
        {
          text: GUIDE_INFO.cancelButton,
          onClick: () => {
            modal.hide();
            handleOnClickCancelBtn(); // 该函数是个异步函数, 但这种情况没必要加 await, 所以没加, 后续如果有必要, 请加上 await
          },
        },
        {
          text: GUIDE_INFO.confirmButton,
          onClick: () => {
            modal.hide();
            handleOnClickConfirmBtn();
          },
        },
      ],
    });
  }

  function handlePopUpTel() {
    analytics.mv(analyticConfig.guideInfo.mv, {
      popup_type: String(GUIDE_INFO.type),
    });
    modal.show({
      title: removeNewlineFromTitle(title),
      content,
      preventScroll: true,
      className: 'guide-info-modal',
      button: [
        {
          text: GUIDE_INFO.cancelButton,
          onClick: () => {
            analytics.addTag(
              {
                bid: analyticConfig.guideInfo.mc,
                from: env.scene,
                utm_source: env.utmSource,
                button_name: GUIDE_INFO.cancelButton,
                popup_type: String(GUIDE_INFO.type),
              },
              cid
            );
            analytics.click(analyticConfig.guideInfo.mc, {
              button_name: GUIDE_INFO.cancelButton,
              popup_type: String(GUIDE_INFO.type),
            });
            modal.hide();
            // 调用取消回调
            if (onCancel) onCancel();
          },
        },
        {
          text: GUIDE_INFO.confirmButton,
          onClick: () => {
            analytics.addTag(
              {
                bid: analyticConfig.guideInfo.mc,
                from: env.scene,
                utm_source: env.utmSource,
                button_name: GUIDE_INFO.confirmButton,
                popup_type: String(GUIDE_INFO.type),
              },
              cid
            );
            analytics.click(analyticConfig.guideInfo.mc, {
              button_name: GUIDE_INFO.confirmButton,
              popup_type: String(GUIDE_INFO.type),
            });
            // 拨打电话95172
            bridge.openPage('tel:95172');
            modal.hide();
            bridge.subscribeAppear(() => {
              refresh();
            });
          },
        },
      ],
    });
  }

  function constraintConfirmButton() {
    bridge.subscribe('appear', () => {
      bridge.unSubscribe('appear', () => {
        getStorage('VERIFIED_RESULT_SUCCESS')
          .then((res) => {
            if (res) {
              removeStorage('VERIFIED_RESULT_SUCCESS');
              refresh();
              if (isToCallBackUrl) bridge.openPage(url);
            }
          })
          .catch(() => {});
      });
    });
    // 跳转信息完善页面
    let targetUrl = GUIDE_INFO.guideUrl.indexOf('?') > -1 ? `${GUIDE_INFO.guideUrl}&` : `${GUIDE_INFO.guideUrl}?`;
    // 提现 sceneId 1002，充值 sceneId 1001
    // https://km.sankuai.com/page/1326653886
    targetUrl += `scene_id=${type === 'withdraw' ? 1002 : 1001}&openType=6`;
    bridge.openPage(targetUrl);
  }

  function balanceFreezeConfirmButton() {
    const goFromFrozen = () => {
      // 跳转冻结页面
      let sceneNum = 0;
      if (extend && extend.from === 'unfreeze') {
        sceneNum = 1003;
      } else {
        sceneNum = type === 'withdraw' ? 1002 : 1001;
      }
      let targetUrl = GUIDE_INFO.guideUrl.indexOf('?') > -1 ? `${GUIDE_INFO.guideUrl}&` : `${GUIDE_INFO.guideUrl}?`;
      // 提现 sceneId 1002，充值 sceneId 1001
      targetUrl += `scene_id=${sceneNum}&openType=6`;
      bridge.openPage(targetUrl);
    };
    if (env.inWeixinMini) {
      goFromFrozen();
    } else {
      bridge.subscribeAppear(
        () => {
          if (isToCallBackUrl) refreshPageSubscribe();
          else refresh();
        },
        () => {
          goFromFrozen();
        }
      );
    }
  }

  function freezePopup() {
    analytics.mv(analyticConfig.guideInfo.mv, {
      popup_type: '3',
    });
    modal.show({
      title: removeNewlineFromTitle(title),
      content,
      preventScroll: true,
      className: 'guide-info-modal',
      button: {
        text: GUIDE_INFO.confirmButton,
        onClick: () => {
          analytics.addTag(
            {
              bid: analyticConfig.guideInfo.mc,
              from: env.scene,
              utm_source: env.utmSource,
              button_name: GUIDE_INFO.confirmButton,
              popup_type: '3',
            },
            cid
          );
          analytics.click(analyticConfig.guideInfo.mc, {
            button_name: GUIDE_INFO.confirmButton,
            popup_type: '3',
          });
          modal.hide();
        },
      },
    });
  }

  function accountConfirmPopup() {
    modal.show({
      title: removeNewlineFromTitle(title),
      content,
      preventScroll: true,
      className: 'account-info-modal',
      closeButton: true,
      onClose: () => {
        modal.hide();
      },
      button: [
        {
          text: GUIDE_INFO.confirmButton,
          onClick: () => {
            analytics.addTag(
              {
                bid: analyticConfig.guideInfo.mc,
                from: env.scene,
                utm_source: env.utmSource,
                button_name: GUIDE_INFO.confirmButton,
                popup_type: '5',
              },
              cid
            );
            analytics.click(analyticConfig.guideInfo.mc, {
              button_name: GUIDE_INFO.confirmButton,
              popup_type: '5',
            });
            modal.hide();
            reportBlackType();
            refresh();
            if (isToCallBackUrl && GUIDE_INFO.canSkip) {
              bridge.openPage(url);
            }
          },
        },
        {
          text: GUIDE_INFO.cancelButton,
          onClick: () => {
            analytics.addTag(
              {
                bid: analyticConfig.guideInfo.mc,
                from: env.scene,
                utm_source: env.utmSource,
                button_name: GUIDE_INFO.cancelButton,
                popup_type: '5',
              },
              cid
            );
            analytics.click(analyticConfig.guideInfo.mc, {
              button_name: GUIDE_INFO.cancelButton,
              popup_type: '5',
            });
            modal.hide();
            const goFromAccountConfirm = () => {
              // 跳转实名页面
              let targetUrl =
                GUIDE_INFO.guideUrl.indexOf('?') > -1 ? `${GUIDE_INFO.guideUrl}&` : `${GUIDE_INFO.guideUrl}?`;
              // 提现 sceneId 1002，充值 sceneId 1001
              targetUrl += `scene_id=${type === 'withdraw' ? 1002 : 1001}&openType=6`;
              bridge.openPage(targetUrl);
            };
            if (env.inWeixinMini) {
              goFromAccountConfirm();
            } else {
              bridge.subscribeAppear(
                () => {
                  refreshPageSubscribe();
                },
                () => {
                  goFromAccountConfirm();
                }
              );
            }
          },
        },
      ],
    });
  }

  function monitorPopup() {
    const pageName = {
      index: '首页',
      recharge: '充值页',
      withdraw: '提现页',
      donate: '余额处理页',
    }[fromPage || 'index'];

    monitor.captureMessage(
      {
        name: `${pageName}管控弹窗`,
        msg: `弹窗类型type: ${GUIDE_INFO?.type}；popupTypeForUser: ${GUIDE_INFO?.popupTypeForUser}；操作类型：${type}`,
      },
      {
        tags: {
          errorCode: GUIDE_INFO?.type || '',
          fromPage,
        },
      }
    );
  }

  switch (true) {
    case GUIDE_INFO.popupTypeForUser === 0:
      refresh();
      if (isToCallBackUrl) bridge.openPage(checkUrl(url), 1);
      if (onCancel) onCancel();
      break;
    case GUIDE_INFO.popupTypeForUser === 1:
      monitorPopup();
      handlePopUpWindow();
      break;
    case GUIDE_INFO.popupTypeForUser === 2:
      monitorPopup();
      handlePopUpTel();
      break;
    default:
      // 这里删掉了之前的老逻辑, 加一个埋点观测一下
      monitor.captureMessage({
        name: '余额管控弹窗走到兜底逻辑',
        msg: `popupTypeForUser: ${GUIDE_INFO.popupTypeForUser}`,
      });
      toast.show('text', '系统异常，请稍后重试');
      break;
  }
}
